import { Trans, useTranslation } from 'react-i18next'

import { createApiBankAccountRepository } from '@/Contract/infrastructure'
import { AccountProvider } from '@/Contract/ui/context'
import { homeTranslation } from '@/Home/ui/homeTranslation'
import { HomeContent } from '@/Home/ui/organisms'
import { createApiInvoiceRepository } from '@/Invoices/infrastructure'
import { InvoiceProvider } from '@/Invoices/ui/context'
import { WebTagPermissionEnum } from '@/Shared/domain'
import { PageWrapper } from '@/Shared/ui/layout'
import { MedalliaTag } from '@/Shared/ui/medalliaTag/MedalliaTag'
import { navigationRoutes } from '@/Shared/ui/routers'
import { createApiTransactionRepository } from '@/Transactions/infrastructure'
import { TransactionProvider } from '@/Transactions/ui/context/TransactionsContext'
import { useWebTagPermissions } from '@/User/context'
import { useUserData } from '@/User/ui/context'

import styles from './HomePage.module.scss'

export const HomePage = (): JSX.Element => {
  const { t } = useTranslation()
  const { userName, merchant } = useUserData()
  const { hasPermission } = useWebTagPermissions()
  const isNursery = Boolean(merchant?.isNursery)
  // Solo montamos InvoiceProvider si Home realmente va a renderizar InvoicesList.
  // Asi evitamos llamadas de facturas cuando el bloque no se muestra.
  const shouldShowInvoicesList =
    !merchant.isMasterGroup && hasPermission(WebTagPermissionEnum.VIEW_SHOW_INVOICES)

  // Solo montamos TransactionProvider en Home si tiene permiso y es child con hard dependency.
  const shouldShowTransactionsList =
    !merchant.isMasterGroup && merchant.isChildWithHardDependency

  const isAdminOrSuperAdmin = hasPermission(
    WebTagPermissionEnum.ACTION_BANK_ACCOUNT_CHANGE,
  )

  // Extraemos el contenido principal para poder envolverlo condicionalmente con providers.
  const content = (
    <PageWrapper
      title={t(homeTranslation.title, {
        merchant: userName,
      })}
      subtitle={
        merchant.isMasterGroup ? (
          <div className={styles.subtitleMaster}>
            <p>
              <Trans t={t}>{t(homeTranslation.subtitleMaster)}</Trans>
            </p>
            <p>
              <Trans t={t}>{t(homeTranslation.secondSubtitleMaster)}</Trans>
            </p>
          </div>
        ) : isNursery ? (
          isAdminOrSuperAdmin ? (
            t(homeTranslation.subtitleNurseryAdmin)
          ) : (
            t(homeTranslation.subtitleNurseryVisor)
          )
        ) : (
          t(homeTranslation.subtitle)
        )
      }
      titleSize="title-md"
    >
      {!merchant.isMasterGroup && <HomeContent />}
    </PageWrapper>
  )

  return (
    <AccountProvider
      merchantId={merchant.id}
      repository={createApiBankAccountRepository()}
      origin={navigationRoutes.home}
    >
      {shouldShowInvoicesList ? (
        // Si hay facturas, Home necesita InvoiceProvider.
        // Ademas, cuando tambien hay transacciones, anidamos TransactionProvider
        // para evitar el error de contexto en TransactionsList (useTransaction sin provider).
        <InvoiceProvider repository={createApiInvoiceRepository()} itemsPerPage={5}>
          {shouldShowTransactionsList ? (
            <TransactionProvider
              repository={createApiTransactionRepository()}
              itemsPerPage={8}
            >
              {content}
            </TransactionProvider>
          ) : (
            content
          )}
        </InvoiceProvider>
      ) : shouldShowTransactionsList ? (
        // Si no hay facturas pero si transacciones, montamos solo TransactionProvider.
        <TransactionProvider
          repository={createApiTransactionRepository()}
          itemsPerPage={8}
        >
          {content}
        </TransactionProvider>
      ) : (
        // Si Home no muestra ninguna tabla, no montamos providers extra.
        content
      )}
      <MedalliaTag />
    </AccountProvider>
  )
}
